import { Box, CssBaseline } from '@mui/material';
import { BrowserRouter } from 'react-router-dom';
import { TelemetryCustomization, AppUrls, track, useGetConfig } from '../logic';
import { Auth0ProviderWithHistory } from '../logic/Auth0ProviderWithHistory';
import { getPageUrl } from '../utils/getPageUrl';
import { OrganizationProvider } from './OrganizationProvider';
import { RoutesComponent } from './Routes';
import { LoadingScreen } from './shared';
import { ErrorBoundary } from './shared/ErrorBoundary';
import { ErrorDialog } from './shared/dialogs/ErrorDialog';
import '../../App.css'
import ScrollToTop from './shared/ScrollToTop';
import { ExternalLinkCapturer } from './shared/ExternalLinkCapturer';
import { Query, QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import '../../dayjs-setup'
import { PinTokenStateProvider } from './context/pin-state/PinState';

// NOTE: We only persist queries whose key includes 'persistLocalStorage' (see dehydrateOptions below)
const persister = createSyncStoragePersister({
  storage: window.localStorage,
})

const dehydrateOptions = {
  // Only dehydrate queries that are successful and include 'persistLocalStorage' in their query key
  shouldDehydrateQuery: (query: Query) => {
    const isSuccessful = query.state.status === 'success'
    if (!isSuccessful) return false
    return query.queryKey.includes('persistLocalStorage')
  },
}

const queryClient = new QueryClient()

export function App() {
  const drawerWidth = 270
  const drawerHeight = 70

  const { loading, error, data } = useGetConfig(getPageUrl())

  if (loading) {
    return <LoadingScreen />

  } else if (error) {
    console.log(`[api] useGetConfig() ERROR: ${JSON.stringify(error)}`);

    track('error', 'useGetConfig', {}, {
      message: JSON.stringify(error),
      component: 'App',
    })

    const redirectToRequestInvite = () => window.location.assign(AppUrls.requestInvite())

    return (
      <ErrorDialog
        error={error}
        onTryAgain={redirectToRequestInvite}
        waitOnAuth0={false}
        tryAgainText='Request Invitation'
      />
    )
  }

  if ('redirectTo' in data!) {
    window.location.assign(data!.redirectTo!)
    return <></>
  }

  const { org, auth0TicketId, companyIdOrderIdHash} = data!

  return (
    <ExternalLinkCapturer>
      <BrowserRouter>
        <ScrollToTop />
        <Auth0ProviderWithHistory organization={org}>
          <PersistQueryClientProvider client={queryClient} persistOptions={{ persister, dehydrateOptions }}>
            <PinTokenStateProvider>
              <ErrorBoundary>
                <OrganizationProvider organization={org}>
                  <>
                    <Box sx={{ height: '100%', minHeight: '100vh' }}>

                      {/* This needs to be called inside of Auth0ProviderWithHistory to allow
                              for adding Auth0 user data to error tracking events. Renders nothing.
                          */}
                      <TelemetryCustomization />

                      <CssBaseline />

                      <Box
                        component="main"
                        sx={{
                          display: { xs: 'block', sm: 'flex' },
                          height: '100%', minHeight: '100vh',
                          width: '100%', mb: '56px',
                          marginTop: { xs: `${drawerHeight}px`, sm: '0'},
                        }}
                      >
                        <RoutesComponent
                          drawerWidth={drawerWidth}
                          drawerHeight={drawerHeight}
                          auth0TicketId={auth0TicketId}
                          companyIdOrderIdHash={companyIdOrderIdHash}
                        />
                      </Box>
                    </Box>
                    <ReactQueryDevtools initialIsOpen={false} />
                  </>
                </OrganizationProvider>
              </ErrorBoundary>
            </PinTokenStateProvider>
          </PersistQueryClientProvider>
        </Auth0ProviderWithHistory>
      </BrowserRouter>
    </ExternalLinkCapturer>
  )
}
